import { useState, useRef, forwardRef, useImperativeHandle, useEffect } from 'react';
import { Send, Paperclip, X, Loader2, Image, FileText, FileSpreadsheet } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useChatUpload } from '@/lib/hooks/use-chat-upload';
import type { MessageInsert } from '@/lib/api/services/chat.service';

interface MessageInputProps {
  conversationId: string;
  onSend: (message: MessageInsert) => void;
  onTypingChange?: (isTyping: boolean) => void;
}

export interface MessageInputRef {
  setValue: (value: string) => void;
}

export const MessageInput = forwardRef<MessageInputRef, MessageInputProps>(({
  conversationId,
  onSend,
  onTypingChange,
}, ref) => {
  const [inputValue, setInputValue] = useState('');
  const [attachedFile, setAttachedFile] = useState<{
    url: string;
    fileName: string;
    fileType: string;
  } | null>(null);
  const [uploadingFile, setUploadingFile] = useState<{
    file: File;
    previewUrl: string;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { uploadFile, isUploading } = useChatUpload();

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      const minHeight = 48; // Minimum height (h-12 equivalent)
      const maxHeight = 300; // Maximum height
      const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);
      textareaRef.current.style.height = `${newHeight}px`;
    }
  }, [inputValue]);

  useImperativeHandle(ref, () => ({
    setValue: (value: string) => {
      setInputValue(value);
    },
  }));

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Create preview URL for images
    const previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : '';

    // Set uploading state with preview
    setUploadingFile({
      file,
      previewUrl,
    });

    try {
      const result = await uploadFile({ file, conversationId });
      setAttachedFile({
        url: result.url,
        fileName: result.file_name,
        fileType: result.file_type,
      });
    } catch (error) {
      console.error('Failed to upload file:', error);
    } finally {
      // Clean up preview URL
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setUploadingFile(null);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileTypeSelect = (accept: string) => {
    if (fileInputRef.current) {
      fileInputRef.current.accept = accept;
      fileInputRef.current.click();
    }
  };

  const handleSend = () => {
    if (!inputValue.trim() && !attachedFile) return;

    const messageData: MessageInsert = {
      content: inputValue.trim() || undefined,
      message_type: attachedFile ? 'file' : 'text',
      file_url: attachedFile?.url || null,
      file_name: attachedFile?.fileName || null,
      file_type: attachedFile?.fileType || null,
    };

    // Clear input immediately for smooth UX (optimistic update)
    setInputValue('');
    setAttachedFile(null);
    onTypingChange?.(false);

    // Send message in background (non-blocking)
    try {
      onSend(messageData);
    } catch (error) {
      console.error('Failed to send message:', error);
      // Note: Error handling is done in the mutation's onError
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);

    // Typing indicator
    if (onTypingChange) {
      onTypingChange(true);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      typingTimeoutRef.current = setTimeout(() => {
        onTypingChange(false);
      }, 2000);
    }
  };

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (uploadingFile?.previewUrl) {
        URL.revokeObjectURL(uploadingFile.previewUrl);
      }
    };
  }, [uploadingFile?.previewUrl]);

  const displayFile = attachedFile || (uploadingFile ? {
    url: uploadingFile.previewUrl || '',
    fileName: uploadingFile.file.name,
    fileType: uploadingFile.file.type,
  } : null);

  return (
    <div className="flex-shrink-0 p-4 border-t border-slate-200">
      {displayFile && (
        <div className="mb-2 p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-2">
          <div className="flex items-center gap-2">
            <Paperclip className="w-4 h-4 text-slate-500 flex-shrink-0" />
            <span className="text-sm flex-1 truncate text-slate-700 font-medium">
              {displayFile.fileName}
            </span>
            {!isUploading && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setAttachedFile(null);
                  if (uploadingFile?.previewUrl) {
                    URL.revokeObjectURL(uploadingFile.previewUrl);
                  }
                  setUploadingFile(null);
                }}
                className="h-6 w-6 p-0"
              >
                <X className="w-3 h-3" />
              </Button>
            )}
          </div>
          {isUploading && (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Loader2 className="w-3 h-3 animate-spin" />
              <span>Uploading...</span>
            </div>
          )}
          {displayFile && displayFile.fileType.startsWith('image/') && displayFile.url && (
            <div className="relative">
              {isUploading && (
                <div className="absolute inset-0 bg-slate-200 animate-pulse rounded-lg" />
              )}
              <img
                src={displayFile.url}
                alt={displayFile.fileName}
                className={`max-w-xs rounded-lg ${isUploading ? 'opacity-50' : ''}`}
              />
            </div>
          )}
        </div>
      )}
      <div className="flex items-center gap-3">
        <div className="flex-1 relative">
          <div className="relative">
            <Textarea
              ref={textareaRef}
              placeholder="Type a message..."
              className="pr-12 rounded-2xl min-h-[48px] max-h-[300px] bg-white border-slate-200 resize-none overflow-hidden"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              disabled={isUploading}
              rows={1}
            />
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleFileSelect}
            />
            <div className="absolute right-2 bottom-2 flex items-center">
              {isUploading ? (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled
                  className="h-8 w-8 p-0"
                >
                  <Loader2 className="w-4 h-4 text-slate-500 animate-spin" />
                </Button>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      title="Attach file"
                    >
                      <Paperclip className="w-4 h-4 text-slate-500" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem
                      onClick={() => handleFileTypeSelect('image/*')}
                      className="cursor-pointer"
                    >
                      <Image className="w-4 h-4 mr-2" />
                      <span>Photos</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleFileTypeSelect('application/pdf,.doc,.docx,.txt,.csv')}
                      className="cursor-pointer"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      <span>Document</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleFileTypeSelect('.xls,.xlsx')}
                      className="cursor-pointer"
                    >
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      <span>Spreadsheet</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </div>
        <Button
          onClick={handleSend}
          disabled={(!inputValue.trim() && !displayFile) || isUploading}
          className="rounded-full h-12 px-6 bg-[#4669ed] hover:bg-[#2b42a3]"
        >
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
});

MessageInput.displayName = 'MessageInput';
